
import React from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Save, Send, Clock } from "lucide-react";
import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface ArticleFormActionsProps {
  isSubmitting: boolean;
  handleCancel: () => void;
  handleSubmit: () => void;
  handleSaveDraft?: () => void;
  isEditMode: boolean;
  isDirty?: boolean;
  lastSaved?: Date;
  status?: string;
  autoSaveEnabled?: boolean;
  toggleAutoSave?: () => void;
}

const ArticleFormActions = ({
  isSubmitting,
  handleCancel,
  handleSubmit,
  handleSaveDraft,
  isEditMode,
  isDirty = false,
  lastSaved,
  status = "draft",
  autoSaveEnabled = true,
  toggleAutoSave
}: ArticleFormActionsProps) => {
  const showSaveDraft = handleSaveDraft && (status === "draft" || !isEditMode);
  
  return (
    <div className="flex flex-col gap-4 mt-6 border-t pt-4 px-6 pb-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          {lastSaved && (
            <>
              <Clock className="h-3.5 w-3.5" />
              <span>Last saved: {format(lastSaved, "MMM d, yyyy 'at' h:mm a")}</span>
            </>
          )}
        </div>
        
        {toggleAutoSave && (
          <div className="flex items-center space-x-2">
            <Switch 
              id="auto-save" 
              checked={autoSaveEnabled} 
              onCheckedChange={toggleAutoSave}
            />
            <Label htmlFor="auto-save" className="text-sm">Auto-save</Label>
          </div>
        )}
      </div>
      
      <div className="flex justify-between">
        <div>
          {showSaveDraft && (
            <Button 
              variant="outline" 
              onClick={handleSaveDraft}
              disabled={isSubmitting || (!isDirty && isEditMode)}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Draft
            </Button>
          )}
        </div>
        
        <div className="flex gap-4">
          <Button 
            variant="outline" 
            onClick={handleCancel} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isEditMode ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                {isEditMode ? "Update Article" : "Create Article"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ArticleFormActions;
